'use client'

import { Loader2 } from 'lucide-react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { z } from 'zod'

import { ConnectLogo } from '@/components/connect/connect-logo'
import { useProductBrand } from '@/providers/product-brand-provider'
import { CONNECT_BRAND } from '@/lib/connect/brand'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import { Label } from '@/components/ui/label'
import { prepareAnonymousAnalyticsContext, trackConnect } from '@/lib/analytics/track'
import { useConnectAuth } from '@/lib/connect/use-connect-auth'
import { getConversionAttribution } from '@/lib/analytics/attribution'
import { getApiBaseUrl } from '@/lib/api-config'
import { capturePartnerId } from '@/lib/partner'
import {
  resolveProductName,
  resolveSignupLogo,
  type PartnerBranding,
} from '@/lib/partner-branding'
import { safeRedirectPath as sanitizeRedirect } from '@/lib/safe-redirect'
import { cn } from '@/lib/utils'

// Unified auth: a single entry handles both sign-up and sign-in. The default
// method emails a 6-digit code — redeeming it logs an existing user in or
// creates the account (create-on-verify, see the auth backend). Password is a
// secondary login method for accounts that already have one.
type Method = 'password' | 'code'
// 'form' collects credentials/email; 'code' enters the 6-digit email code.
type Phase = 'form' | 'code'

const RESEND_COOLDOWN_SECONDS = 60

// A partner-install handoff reference (Shoplazza bind code) arrives as a query
// param on /auth. It is forwarded verbatim so the SERVER can classify the
// signup's acquisition source against the live install stash — browser
// first-touch storage does not survive a sign-out or a device switch mid-funnel.
// Shape-checked here only to keep junk out of the request; the API re-validates.
const INSTALL_REF_RE = /^[A-Za-z0-9_-]{16,128}$/

function readInstallRef(raw: string | null): string | undefined {
  const value = raw?.trim()
  if (!value || !INSTALL_REF_RE.test(value)) return undefined
  return value
}

function buildAttributedGoogleHref(
  googleHref: string,
  installRef?: string,
): string {
  const attribution = getConversionAttribution()
  const url = new URL(googleHref, window.location.origin)
  const params: Record<string, string | undefined> = {
    marketing_consent: attribution.marketing_consent?.toString(),
    analytics_consent: attribution.analytics_consent?.toString(),
    gclid: attribution.gclid,
    gbraid: attribution.gbraid,
    wbraid: attribution.wbraid,
    fbc: attribution.fbc,
    fbp: attribution.fbp,
    event_source_url: attribution.event_source_url,
    utm: attribution.utm ? JSON.stringify(attribution.utm) : undefined,
    gravity: attribution.gravity
      ? JSON.stringify(attribution.gravity)
      : undefined,
    referral_code: attribution.referral_code,
    // Co-branding attribution. Must be forwarded by the begin route too — this
    // is one of three independent allowlists the value has to cross, and a miss
    // at any of them means a Google signup from a partner's own page silently
    // lands in Soku-branded product with no error anywhere.
    partner_id: attribution.partner_id,
    // Rides to the API host and into the Redis state, never to Google.
    install_ref: installRef,
  }
  for (const [key, value] of Object.entries(params)) {
    if (value) url.searchParams.set(key, value)
  }
  return `${url.pathname}${url.search}`
}

// Google's brand mark for the OAuth button (no lucide equivalent).
function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-4">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"
      />
    </svg>
  )
}

const emailSchema = z.email({ message: 'Enter a valid email address' })
const passwordSchema = z.string().min(1, { message: 'Password is required' })

// Map a backend domain code to user copy. The machine-readable reason is in
// `error.code` (see auth_self._flow_error); `error.message` is diagnostic
// only. Google OAuth (?error=) also keys on `code`; the rate limiter returns
// its reason in `detail`.
const ERROR_COPY: Record<string, string> = {
  // Uniform anti-enumeration error: the backend can't tell a wrong password
  // from a missing account, so the copy must not assert either. It's phrased as
  // an action for the common case — a new user who reached the password field
  // expecting to sign up — and routes everyone to the code path (which both
  // creates accounts and signs returning users in).
  invalid_credentials:
    "That didn't work. New to Soku or don't have a password yet? Continue with a one-time code instead — no password needed.",
  password_policy: 'Password does not meet the requirements.',
  rate_limited: 'Too many attempts. Please wait a moment and try again.',
  // Google OAuth failures bounce back to /auth?error=<reason> (see the BFF
  // finish route and the API callback).
  oauth_failed: 'Google sign-in failed. Please try again.',
  oauth_state: 'Your Google sign-in session expired. Please try again.',
  oauth_unavailable:
    'Google sign-in is temporarily unavailable. Please try again later.',
}

// On the code-entry screen, invalid_credentials means a bad/expired code, not
// a wrong password — give a screen-appropriate message.
const CODE_ERROR_COPY: Record<string, string> = {
  ...ERROR_COPY,
  invalid_credentials: 'That code is incorrect or has expired.',
}

type ApiResult = {
  ok: boolean
  data: {
    success?: boolean
    error?: { code?: string; message?: string }
    detail?: unknown
  } | null
}

function pickErrorMessage(
  data: ApiResult['data'],
  table: Record<string, string>,
): string {
  // Match on the machine-readable code (the rate limiter uses `detail`); the
  // raw message remains diagnostic when no copy is mapped.
  const candidates = [
    data?.error?.code,
    typeof data?.detail === 'string' ? data.detail : undefined,
  ]
  const matched = candidates.find(
    (value): value is string => Boolean(value) && Object.hasOwn(table, value!),
  )
  return (
    (matched && table[matched]) ||
    'Something went wrong. Please try again.'
  )
}

export function SelfAuthForm({
  partner = null,
  heading,
  layout = 'page',
}: {
  /**
   * Replaces the default "Your AI Marketer that Never Sleeps" block.
   *
   * The tool dialogs mount this form under a DialogHeader that already says
   * why the visitor is being asked for an account ("Sign up to generate ... with
   * Seedance 2.5"). Rendering the generic pitch underneath gave every one of
   * them two stacked titles and two descriptions, with the specific one
   * shrunk above the generic one. They now pass their copy down here instead,
   * so the dialog has a single heading and it is the one that answers the
   * question the visitor actually has.
   */
  heading?: { title: ReactNode; description?: ReactNode }
  /**
   * `page` is the standalone /auth surface, where the serif display heading
   * carries the brand. `dialog` is the in-tool modal, where that same 38px
   * serif was louder than the tool page behind it and pushed the actual form
   * below the fold — so the identity block shrinks to a sans heading and the
   * logo drops to the size of a favicon.
   */
  layout?: 'page' | 'dialog'
  /**
   * Co-branding for a partner's signup page; null on the plain /auth entry.
   *
   * Passed in rather than read from the active-org context: this form renders
   * before a session exists, so there is no org to read a partner off. The page
   * resolved it server-side from the slug in the URL.
   */
  partner?: PartnerBranding | null
} = {}) {
  const searchParams = useSearchParams()
  const productBrand = useProductBrand()
  const connect = !partner && productBrand.product === 'toolfare'
  const errorCopy = connect
    ? {
        ...ERROR_COPY,
        invalid_credentials: `That didn't work. New to ${CONNECT_BRAND.name} or don't have a password yet? Continue with a one-time code instead — no password needed.`,
      }
    : ERROR_COPY
  const codeErrorCopy = connect
    ? { ...CODE_ERROR_COPY, invalid_credentials: errorCopy.invalid_credentials }
    : CODE_ERROR_COPY
  // Post-sign-in redirect (e.g. back to an invite-accept link). Sanitized.
  const redirectPath = sanitizeRedirect(searchParams.get('redirect_url')) || (productBrand.product === 'toolfare' ? '/connect/start' : '/')
  const installRef = readInstallRef(searchParams.get('install_ref'))

  // Google sign-in entrypoint: the same-origin BFF /begin route (it sets the
  // login-CSRF nonce cookie, then redirects to the API host). Hidden when the
  // backend endpoint isn't configured.
  const apiBase = getApiBaseUrl().replace(/\/$/, '')
  const connectAuth = useConnectAuth()
  const authTrack = (event: string, props?: Record<string, unknown>) => {
    if (connectAuth || connect) {
      prepareAnonymousAnalyticsContext()
      trackConnect(event, props)
    }
  }
  const googleHref = apiBase
    ? `/api/session/oauth/google/begin${
        redirectPath !== '/'
          ? `?redirect_url=${encodeURIComponent(redirectPath)}`
          : ''
      }`
    : ''
  const [attributedGoogleHref, setAttributedGoogleHref] = useState(googleHref)

  useEffect(() => {
    if (!googleHref) return
    try {
      setAttributedGoogleHref(buildAttributedGoogleHref(googleHref, installRef))
    } catch {
      setAttributedGoogleHref(googleHref)
    }
  }, [googleHref, installRef])

  // Best-effort: attach ad-click identifiers to the OAuth /begin navigation so
  // the user_created conversion can be attributed after the round-trip (the
  // Gravity getCAPIData blob is dropped — it can't survive the cross-domain
  // callback). Falls back to the plain href on modified clicks or any error.
  function handleGoogleClick(event: React.MouseEvent<HTMLAnchorElement>) {
    if (event.defaultPrevented) return
    authTrack('connect_signup_started', { method: 'google' })
    authTrack('connect_google_signin_started')
    try {
      // Same capture point as the email form: following the Google button is
      // starting a signup on this partner's page. Must run before the href is
      // rebuilt, since the attribution snapshot reads the cookie.
      if (partner) capturePartnerId(partner.id)
      const href = buildAttributedGoogleHref(googleHref, installRef)
      event.currentTarget.href = href
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return
      }
      event.preventDefault()
      window.location.assign(href)
    } catch {
      // Fall back to the plain href navigation.
    }
  }
  const oauthError = searchParams.get('error')

  const [method, setMethod] = useState<Method>('code')
  const [phase, setPhase] = useState<Phase>('form')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(
    oauthError && Object.hasOwn(ERROR_COPY, oauthError) ? ERROR_COPY[oauthError] : null,
  )
  const [submitting, setSubmitting] = useState(false)
  const [resendIn, setResendIn] = useState(0)

  // Resend cooldown ticker (code phase only).
  useEffect(() => {
    if (resendIn <= 0) return
    const timer = setInterval(() => {
      setResendIn(value => (value <= 1 ? 0 : value - 1))
    }, 1000)
    return () => clearInterval(timer)
  }, [resendIn])

  async function postJson(
    endpoint: string,
    payload: Record<string, unknown>,
  ): Promise<ApiResult> {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    })
    const data = (await response.json().catch(() => null)) as ApiResult['data']
    return { ok: response.ok && Boolean(data?.success), data }
  }

  async function handleFormSubmit(event: React.FormEvent) {
    event.preventDefault()
    setFieldError(null)
    setFormError(null)

    const parsedEmail = emailSchema.safeParse(email)
    if (!parsedEmail.success) {
      setFieldError(parsedEmail.error.issues[0]?.message ?? 'Invalid input')
      return
    }

    // Attribution is captured here — at the point the visitor actually starts
    // signing up on the partner's page — rather than when the page loaded.
    // Referral uses last-click-on-any-pageview because what it grants is a
    // credit bonus; partner attribution eventually grants a partner's operator
    // admin on the workspace this person creates, with its credentials,
    // conversations and ad data. "Glanced at a partner page once, signed up from
    // the normal entry three weeks later" must not silently produce that.
    if (partner) capturePartnerId(partner.id)
    if (method === 'password') {
      const parsedPassword = passwordSchema.safeParse(password)
      if (!parsedPassword.success) {
        setFieldError(
          parsedPassword.error.issues[0]?.message ?? 'Invalid input',
        )
        return
      }
    }

    setSubmitting(true)
    try {
      if (method === 'password') {
        // Password is login-only — there is no password sign-up. An unknown
        // email returns invalid_credentials, whose copy nudges to the code.
        const { ok, data } = await postJson('/api/session/login', {
          email: parsedEmail.data,
          password,
        })
        if (ok) {
          window.location.assign(redirectPath)
          return
        }
        setFormError(pickErrorMessage(data, errorCopy))
        return
      }

      authTrack('connect_signup_started', { method: 'email_code' })
      // Code method: request a one-time code, then move to the code screen.
      // The same code logs an existing user in or creates a new account.
      const { ok, data } = await postJson('/api/session/email-code/request', {
        email: parsedEmail.data,
      })
      if (ok) {
        setCode('')
        setResendIn(RESEND_COOLDOWN_SECONDS)
        authTrack('connect_verification_code_requested', { resend: false })
        setPhase('code')
        return
      }
      setFormError(pickErrorMessage(data, errorCopy))
    } catch {
      setFormError('Network error. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleVerifySubmit(event: React.FormEvent) {
    event.preventDefault()
    setFormError(null)
    if (code.length !== 6) {
      setFormError('Enter the 6-digit code.')
      return
    }
    setSubmitting(true)
    try {
      const { ok, data } = await postJson('/api/session/email-code/verify', {
        email,
        code,
        // Best-effort ad-click attribution for the user_created conversion;
        // the backend degrades gracefully when fields are missing.
        attribution: getConversionAttribution(),
        // Server-side acquisition anchor; see readInstallRef above.
        install_ref: installRef,
      })
      if (ok) {
        window.location.assign(redirectPath)
        return
      }
      setFormError(pickErrorMessage(data, codeErrorCopy))
    } catch {
      setFormError('Network error. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleResend() {
    if (resendIn > 0 || submitting) return
    setFormError(null)
    setSubmitting(true)
    try {
      const { ok, data } = await postJson('/api/session/email-code/request', {
        email,
      })
      if (ok) authTrack('connect_verification_code_requested', { resend: true })
      // A successful request is always 200 (anti-enumeration), so only then
      // start the cooldown. On a real failure (rate limit / CSRF)
      // surface the reason instead of silently locking the button for 60s.
      if (ok) {
        setResendIn(RESEND_COOLDOWN_SECONDS)
      } else {
        setFormError(pickErrorMessage(data, codeErrorCopy))
      }
    } catch {
      setFormError('Network error. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  function backToForm() {
    setPhase('form')
    setCode('')
    setFormError(null)
  }

  // Switch between the code and password methods and clear any error surfaced
  // by the previous method. Used by both the bottom toggle and the
  // password-failure recovery affordance.
  function switchMethod(next: Method) {
    setMethod(next)
    setFieldError(null)
    setFormError(null)
  }

  // ── Code-entry screen ────────────────────────────────────────────────
  if (phase === 'code') {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2 text-center">
          <h2 className="text-foreground font-serif text-3xl font-bold tracking-tight">
            Enter your code
          </h2>
          <p className="text-muted-foreground text-sm">
            We sent a 6-digit code to{' '}
            <span className="font-medium">{email}</span>.
          </p>
        </div>

        <form
          className="flex flex-col items-center gap-4"
          onSubmit={handleVerifySubmit}
        >
          <InputOTP
            maxLength={6}
            value={code}
            onChange={setCode}
            disabled={submitting}
            containerClassName="justify-center"
            aria-label="Email code"
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} className="size-12 text-base" />
              <InputOTPSlot index={1} className="size-12 text-base" />
              <InputOTPSlot index={2} className="size-12 text-base" />
              <InputOTPSlot index={3} className="size-12 text-base" />
              <InputOTPSlot index={4} className="size-12 text-base" />
              <InputOTPSlot index={5} className="size-12 text-base" />
            </InputOTPGroup>
          </InputOTP>

          {formError && (
            <p role="alert" className="text-destructive text-sm">
              {formError}
            </p>
          )}

          <Button
            type="submit"
            className="h-12 w-full rounded-full"
            disabled={submitting || code.length !== 6}
          >
            {submitting ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Verifying…
              </>
            ) : (
              'Verify'
            )}
          </Button>
        </form>

        <div className="text-muted-foreground flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={backToForm}
            className="hover:text-foreground underline"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleResend}
            disabled={resendIn > 0 || submitting}
            className="hover:text-foreground underline disabled:cursor-not-allowed disabled:opacity-50"
          >
            {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
          </button>
        </div>
      </div>
    )
  }

  // ── Credential / email screen ────────────────────────────────────────
  const showPasswordField = method === 'password'

  const compact = layout === 'dialog'

  return (
    <div className={cn('flex flex-col', compact ? 'gap-5' : 'gap-6')}>
      <div
        className={cn(
          'flex flex-col items-start text-start',
          compact ? 'gap-4' : 'gap-5',
        )}
      >
        {connect ? (
          <ConnectLogo tone="theme" className={compact ? 'text-xl' : 'text-3xl'} />
        ) : (
          <img
            src={resolveSignupLogo(partner, productBrand)}
            alt={resolveProductName(partner, productBrand)}
            className={cn('w-auto', compact ? 'h-7' : 'h-10')}
          />
        )}
        <div className="flex flex-col gap-2">
          <h2
            className={cn(
              'text-foreground tracking-tight',
              compact
                ? 'font-sans text-2xl leading-tight'
                : 'font-serif text-[38px] leading-[1.12]',
            )}
          >
            {heading?.title ?? (connect ? (
              `Continue with ${CONNECT_BRAND.name}`
            ) : (
              <>
                Your AI Marketer
                <br />
                that Never Sleeps
              </>
            ))}
          </h2>
          <p
            className={cn(
              'text-muted-foreground',
              compact ? 'text-sm leading-6' : 'text-base',
            )}
          >
            {heading?.description ??
              (connect
                ? CONNECT_BRAND.tagline
                : 'Soku runs your marketing 24/7, so you can focus on what matters.')}
          </p>
        </div>
      </div>

      {googleHref && (
        <>
          <Button
            asChild
            variant="outline"
            className="h-12 w-full rounded-full"
            disabled={submitting}
          >
            <a href={attributedGoogleHref} onClick={handleGoogleClick}>
              <GoogleIcon />
              Continue with Google
            </a>
          </Button>
          <div className="flex items-center gap-3">
            <span className="bg-border h-px flex-1" />
            <span className="text-muted-foreground text-xs">or</span>
            <span className="bg-border h-px flex-1" />
          </div>
        </>
      )}

      <form
        className="flex flex-col gap-4"
        onSubmit={handleFormSubmit}
        noValidate
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={event => setEmail(event.target.value)}
            disabled={submitting}
            required
            className="h-12 rounded-xl px-4"
          />
        </div>

        {showPasswordField && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link
                href="/reset-password"
                className="text-muted-foreground hover:text-foreground text-sm underline"
              >
                Forgot password?
              </Link>
            </div>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={event => setPassword(event.target.value)}
              disabled={submitting}
              required
              className="h-12 rounded-xl px-4"
            />
            {/* Password is login-only (no password sign-up) — set expectations
                before a new user types a password they never created. */}
            <p className="text-muted-foreground text-xs">
              For accounts that already have a password.
            </p>
          </div>
        )}

        {fieldError && <p className="text-destructive text-sm">{fieldError}</p>}
        {formError && (
          <div className="flex flex-col gap-2">
            <p role="alert" className="text-destructive text-sm">
              {formError}
            </p>
            {/* Turn a failed password sign-in into a recovery path: a new user
                (or anyone without a password) jumps to the code method with
                their email preserved, instead of hitting a dead end. */}
            {method === 'password' && (
              <button
                type="button"
                onClick={() => switchMethod('code')}
                className="text-foreground self-start text-sm font-medium underline"
              >
                Email me a code instead <span aria-hidden>→</span>
              </button>
            )}
          </div>
        )}

        <Button
          type="submit"
          className="h-12 w-full rounded-full"
          disabled={submitting}
        >
          {submitting ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              {method === 'code' ? 'Sending…' : 'Signing in…'}
            </>
          ) : method === 'code' ? (
            'Continue'
          ) : (
            'Sign in'
          )}
        </Button>

        {/* Sits inside the form so it reads as attached to the primary CTA;
            it covers the Google button above it too — this screen is the only
            way into an account. */}
        <p className="text-muted-foreground text-center text-xs leading-5">
          By continuing, you agree to {resolveProductName(partner, productBrand)}&rsquo;s{' '}
          <Link href="/terms" className="hover:text-foreground underline">
            Terms &amp; Conditions
          </Link>{' '}
          and{' '}
          <Link href="/privacy" className="hover:text-foreground underline">
            Privacy Policy
          </Link>
          .
        </p>
      </form>

      <button
        type="button"
        onClick={() =>
          switchMethod(method === 'password' ? 'code' : 'password')
        }
        className="text-muted-foreground hover:text-foreground text-center text-sm underline"
      >
        {method === 'code'
          ? 'Sign in with a password'
          : `New to ${connect ? CONNECT_BRAND.name : 'Soku'}? Email me a code`}
      </button>
    </div>
  )
}
